import React, { useMemo, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import { getConfigTenant, isVesperTenant } from "@capone/common";
import {
  ExperiencesAgeBandEnum,
  ExperiencesPriceBreakdown,
  FiatPrice,
  RewardsAccount,
  RewardsPrice,
  TravelWalletOffer,
} from "redmond";
import {
  CurrencyFormatters,
  getRewardText,
  Icon,
  IconName,
  getPriceString,
  quickPluralize,
  ActionLink,
  DesktopPopupModal,
  twoDecimalFormatter,
  pluralize,
  Tooltip,
} from "halifax";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../context/experiments";

import "./styles.scss";
import { PricingRow } from "./components/PricingRow";
import {
  TAXES_AND_FEES_INCLUDED,
  CANCELLATION_POLICY,
  FREE_CANCELLATION,
  VIEW_POLICY_DETAILS,
  MANDATORY_FEES_TOOLTIP_TEXT,
  MANDATORY_FEES_BELOW,
} from "../textConstants";
import {
  IShopCancellationPolicyProps,
  ShopCancellationPolicy,
} from "../../shop/components/ShopCancellationPolicy/component";

export interface ExperiencesPricingBreakdownProps {
  className?: string;
  priceBreakdown?: ExperiencesPriceBreakdown;
  largestValueAccount?: RewardsAccount;
  isFreeCancellation?: boolean;
  cancellationPolicyProps?: IShopCancellationPolicyProps | null;
  mobile?: boolean;
  creditsAmount?: number;
  offerToApply?: TravelWalletOffer;
  rewardsPaymentInFiatCurrency?: FiatPrice;
  rewardsPaymentInRewardsCurrency?: RewardsPrice;
  cardPaymentAmountRequired?: number;
  cardPaymentLastFour?: string;
  showAmountDueLineItem?: boolean;
  isAfterCardPaymentStep?: boolean;
}

export type SummaryLineType =
  // | "markdown"
  "payment" | "rewards" | "total" | "custom";

// export interface ICreditLineItem {
//   amount: number;
//   currency: string;
//   icon?: IconName;
//   label: string;
//   labelProps?: TypographyProps;
//   valueProps?: TypographyProps;
// }

export interface IExperiencesPricingLineItem {
  lineTitle?: string;
  amount?: number;
  skipIfPriceUnavailable?: boolean;
  icon?: IconName;
  label: string;
}

interface IExperiencesSummaryLineItemBase {
  type: SummaryLineType;
  fiatPrice: FiatPrice;
  tooltipText?: string;
}

// export interface IMarkdownLineItem extends ISimplifiedSummaryLineItemBase {
//   type: "markdown";
//   className?: string;
//   label: string;
//   markdownType: PaymentTypeEnum;
//   redemptionRate?: number;
//   rewardsPrice?: RewardsPrice;
// }

export interface IExperiencesTotalLineItem
  extends IExperiencesSummaryLineItemBase {
  type: "total";
  label?: string;
  details?: string;
  rewardsPrice?: RewardsPrice;
}

export interface IRewardsLineItem extends IExperiencesSummaryLineItemBase {
  type: "rewards";
  rewardsAccountName: string;
  rewardsPrice?: RewardsPrice;
}

export interface IPaymentLineItem extends IExperiencesSummaryLineItemBase {
  type: "payment";
  label?: string;
  lastFour?: string;
}

export interface ICustomLineItem extends IExperiencesSummaryLineItemBase {
  type: "custom";
  icon?: IconName;
  label: string;
  rewardsPrice?: RewardsPrice;
  className?: string;
  travelCreditBreakdown?: {
    label: string;
    formattedAmount: string;
  }[];
}

export type IExperiencesSummaryLineItem =
  | IExperiencesTotalLineItem
  | IRewardsLineItem
  | IPaymentLineItem
  | ICustomLineItem;

const renderPricingItem = ({
  index,
  amount,
  skipIfPriceUnavailable,
  currencyCode,
  label,
}: IExperiencesPricingLineItem & {
  index: number;
  currencyCode: string;
}): JSX.Element | null => {
  const renderPricingRow = (): JSX.Element | null => {
    if (amount === undefined && skipIfPriceUnavailable) {
      return null;
    }

    return (
      <PricingRow
        className="fees"
        amount={amount}
        currency={currencyCode}
        label={label}
      />
    );
  };

  return (
    <Box className="items-section" key={index}>
      <Box className="pricing-item-body">
        <Box className="content-section">{renderPricingRow()}</Box>
      </Box>
    </Box>
  );
};

const renderSummaryItem = (
  index: number,
  summaryLineItem: IExperiencesSummaryLineItem,
  showRowIcon: boolean,
): JSX.Element | null => {
  const isVesper = isVesperTenant(getConfigTenant());
  const { type: summaryType } = summaryLineItem;
  let leftSectionContent = "";
  let rightSectionContent: {
    fiatAmountText: string;
    rewardsAmountText?: string;
    totalAmountText?: string;
  } | null = null;
  let rowIcon: IconName | undefined;
  let valueClassName: string | undefined = "";

  switch (summaryLineItem.type) {
    case "total": {
      const { fiatPrice, label, rewardsPrice } = summaryLineItem;

      leftSectionContent = label ?? "Total";
      rightSectionContent = {
        fiatAmountText: `${CurrencyFormatters.get(
          fiatPrice.currencyCode,
        ).format(fiatPrice.value)}`,
        rewardsAmountText: rewardsPrice
          ? getRewardText({
              reward: rewardsPrice,
            })
          : undefined,
      };
      break;
    }
    case "rewards": {
      const { fiatPrice, rewardsAccountName, rewardsPrice } = summaryLineItem;

      leftSectionContent = `${rewardsAccountName} rewards applied`;
      rightSectionContent = {
        fiatAmountText: `-${CurrencyFormatters.get(
          fiatPrice.currencyCode,
        ).format(fiatPrice.value)}`,
        rewardsAmountText: rewardsPrice
          ? `-${getRewardText({
              reward: rewardsPrice,
            })}`
          : undefined,
      };
      break;
    }
    case "payment": {
      const { fiatPrice, label, lastFour } = summaryLineItem;

      if (label) {
        leftSectionContent = label;
      } else if (lastFour) {
        leftSectionContent = `Payment card ending in ${lastFour}`;
      } else {
        leftSectionContent = "Payment";
      }
      rightSectionContent = {
        fiatAmountText: `${CurrencyFormatters.get(
          fiatPrice.currencyCode,
        ).format(fiatPrice.value)}`,
      };
      rowIcon = IconName.Payment;
      break;
    }
    case "custom": {
      const { fiatPrice, icon, label, rewardsPrice, className } =
        summaryLineItem;

      leftSectionContent = label;
      rightSectionContent = {
        fiatAmountText: CurrencyFormatters.get(fiatPrice.currencyCode).format(
          fiatPrice.value,
        ),
        rewardsAmountText: rewardsPrice
          ? getRewardText({
              reward: rewardsPrice,
            })
          : undefined,
      };
      rowIcon = icon;
      valueClassName = className;
      break;
    }
    default: {
      return null;
    }
  }

  if (!leftSectionContent || !rightSectionContent) {
    return null;
  }

  const { fiatAmountText, rewardsAmountText } = rightSectionContent;
  const isTotal = summaryType === "total" && leftSectionContent === "Total";

  return (
    <Box className={clsx("summary-item-wrapper")} key={index}>
      <Box className={clsx("summary-item-line-wrapper", { vesper: isVesper })}>
        <Box
          className={clsx("summary-line-section", "left", summaryType, {
            "grand-total": isTotal,
          })}
        >
          <Typography
            className="label"
            component="p"
            variant={summaryType === "total" ? "h6" : "subtitle2"}
          >
            {rowIcon && showRowIcon && <Icon className="row-icon" name={rowIcon} />}
            {leftSectionContent}
            {summaryLineItem.tooltipText && (
              <Tooltip
                title={leftSectionContent}
                content={summaryLineItem.tooltipText}
              >
                <Box
                  className="price-breakdown-tooltip"
                  aria-label="Price information"
                >
                  <Icon name={IconName.InfoCircle} />
                </Box>
              </Tooltip>
            )}
            {summaryType === "total" && "details" in summaryLineItem && (
              <span className="label-details">{summaryLineItem.details}</span>
            )}
          </Typography>
        </Box>
        <Box
          className={clsx(
            "summary-line-section",
            "right",
            summaryLineItem.type,
            { "grand-total": isTotal },
          )}
        >
          <Typography
            className={clsx("value", "fiat", valueClassName)}
            variant="h6"
            component="p"
          >
            {fiatAmountText}
          </Typography>
          {rewardsAmountText && (
            <>
              <Typography
                className="separator"
                variant="subtitle2"
                component="p"
              >
                /
              </Typography>
              <Typography
                className="value rewards"
                variant="subtitle2"
                component="p"
              >
                {rewardsAmountText}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const ExperiencesPricingBreakdown = (
  props: ExperiencesPricingBreakdownProps,
): JSX.Element => {
  const {
    className,
    priceBreakdown,
    largestValueAccount,
    isFreeCancellation,
    cancellationPolicyProps,
    mobile,
    rewardsPaymentInFiatCurrency,
    rewardsPaymentInRewardsCurrency,
    creditsAmount,
    offerToApply,
    cardPaymentAmountRequired,
    cardPaymentLastFour,
    showAmountDueLineItem,
    isAfterCardPaymentStep = false,
  } = props;

  const expState = useExperiments();
  const isVesper = isVesperTenant(getConfigTenant());
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const [openCancellationPolicyModal, setOpenCancellationPolicyModal] =
    useState(false);

  const currencyCode = useMemo(() => {
    if (!priceBreakdown) return "USD";
    return priceBreakdown.totalPriceWithFees.fiat.currencyCode;
  }, [priceBreakdown]);

  const currencySymbol = useMemo(() => {
    if (!priceBreakdown) return "$";
    return priceBreakdown.totalPriceWithFees.fiat.currencySymbol;
  }, [priceBreakdown]);

  const pricingItems: IExperiencesPricingLineItem[] = useMemo(() => {
    if (!priceBreakdown) return [];
    const priceItems = priceBreakdown.lineItems.map(
      (item): IExperiencesPricingLineItem => ({
        label: `${getPriceString({
          price: item.specificAgeBand.pricePerTraveler.fiat.value,
          currencySymbol:
            item.specificAgeBand.pricePerTraveler.fiat.currencySymbol,
          priceFormatter: twoDecimalFormatter,
        })} x ${item.specificAgeBand.numberOfTravelers} ${
          item.specificAgeBand.ageBand === ExperiencesAgeBandEnum.CHILD
            ? pluralize(
                item.specificAgeBand.numberOfTravelers,
                item.specificAgeBand.ageBand.toLowerCase(),
                "children",
              )
            : quickPluralize(
                item.specificAgeBand.numberOfTravelers,
                item.specificAgeBand.ageBand.toLowerCase(),
              )
        }`,
        amount: item.totalPriceForAgeBand.fiat.value,
      }),
    );
    return priceItems;
  }, [priceBreakdown]);

  const summaryItems: IExperiencesSummaryLineItem[] = useMemo(() => {
    if (!priceBreakdown) return [];
    let totalItem: IExperiencesTotalLineItem | undefined;
    let rewardItem: IRewardsLineItem | undefined;
    let travelCreditItem: ICustomLineItem | undefined;
    let travelOfferItem: ICustomLineItem | undefined;
    let paymentItem: IPaymentLineItem | undefined;
    let mandatoryFeesItems: (ICustomLineItem | IExperiencesTotalLineItem)[] =
      [];

    if (priceBreakdown.feesDueUponArrival) {
      mandatoryFeesItems = [
        {
          type: "total",
          fiatPrice: {
            value: priceBreakdown.totalPriceWithFees.fiat.value,
            currencyCode,
            currencySymbol,
          },
          label: "Due today",
          tooltipText: `${TAXES_AND_FEES_INCLUDED} ${MANDATORY_FEES_BELOW}`,
        },
        ...priceBreakdown.feesDueUponArrival.mandatoryExtraChargeItems.map(
          ({ chargeDescription, totalAmount }) => ({
            type: "custom" as const,
            fiatPrice: {
              value: totalAmount,
              currencyCode,
              currencySymbol,
            },
            label: chargeDescription,
            className: "fee",
          }),
        ),
        {
          type: "total",
          fiatPrice: {
            value: priceBreakdown.feesDueUponArrival.totalMandatoryExtraCharges,
            currencyCode,
            currencySymbol,
          },
          label: "Fees due at arrival",
          tooltipText: MANDATORY_FEES_TOOLTIP_TEXT,
        },
        {
          type: "total",
          fiatPrice: {
            value:
              priceBreakdown.feesDueUponArrival
                .totalPriceWithMandatoryExtraCharges,
            currencyCode,
            currencySymbol,
          },
        },
      ];
    } else {
      totalItem = {
        type: "total",
        fiatPrice: priceBreakdown.totalPriceWithFees.fiat,
        rewardsPrice: largestValueAccount
          ? priceBreakdown.totalPriceWithFees.rewards[
              largestValueAccount?.accountReferenceId
            ]
          : undefined,
        tooltipText: TAXES_AND_FEES_INCLUDED,
      };
    }

    if (creditsAmount) {
      travelCreditItem = {
        type: "custom",
        fiatPrice: {
          value: creditsAmount,
          currencyCode,
          currencySymbol,
        },
        label: "Travel credit",
        icon: IconName.PiggyBank,
      };
    }

    if (offerToApply && !!offerToApply?.amount.amount) {
      travelOfferItem = {
        type: "custom",
        label: "Travel offer applied",
        fiatPrice: {
          value: offerToApply.amount.amount,
          currencyCode,
          currencySymbol,
        },
        icon: IconName.OfferTag,
      };
    }

    if (largestValueAccount && rewardsPaymentInFiatCurrency) {
      rewardItem = {
        type: "rewards",
        fiatPrice: rewardsPaymentInFiatCurrency,
        rewardsPrice: rewardsPaymentInRewardsCurrency,
        rewardsAccountName: largestValueAccount.productDisplayName,
      };
    }

    if (cardPaymentAmountRequired && cardPaymentLastFour) {
      paymentItem = {
        type: "payment",
        fiatPrice: {
          value: cardPaymentAmountRequired,
          currencyCode,
          currencySymbol,
        },
        lastFour: cardPaymentLastFour,
      };
    } else if (showAmountDueLineItem) {
      paymentItem = {
        type: "payment",
        fiatPrice: {
          value: isAfterCardPaymentStep
            ? cardPaymentAmountRequired || 0
            : priceBreakdown.totalPriceWithFees.fiat.value -
              Math.abs(offerToApply?.amount.amount ?? 0) -
              Math.abs(creditsAmount ?? 0),
          currencyCode,
          currencySymbol,
        },
        label: "Amount due",
      };
    }

    return [
      totalItem,
      ...mandatoryFeesItems,
      travelCreditItem,
      travelOfferItem,
      rewardItem,
      paymentItem,
    ].filter(
      (
        item,
      ): item is
        | IExperiencesTotalLineItem
        | IRewardsLineItem
        | ICustomLineItem
        | IPaymentLineItem => !!item,
    );
  }, [
    largestValueAccount,
    priceBreakdown,
    rewardsPaymentInFiatCurrency,
    creditsAmount,
    offerToApply,
    cardPaymentAmountRequired,
    cardPaymentLastFour,
  ]);

  return (
    <>
      <Box
        className={clsx(className, "experiences-pricing-breakdown", {
          xmod: isXmodRedesign,
        })}
      >
        <Typography
          className="experiences-pricing-breakdown-title"
          variant="h6"
        >
          Checkout breakdown
        </Typography>
        {isFreeCancellation && !mobile && (
          <Box className="experiences-shop-checkout-breakdown-cancellation-policy">
            <Typography className="experiences-shop-checkout-breakdown-cancellation-policy-text">
              <Icon name={IconName.CheckCircleFilledGreen} />
              {FREE_CANCELLATION}
            </Typography>
            <ActionLink
              onClick={() => {
                setOpenCancellationPolicyModal(true);
              }}
              className="experiences-shop-checkout-breakdown-cancellation-policy-button"
              content={
                <Typography className="experiences-shop-checkout-breakdown-cancellation-policy-text">
                  {VIEW_POLICY_DETAILS}
                  <Icon name={IconName.InfoCircle} />
                </Typography>
              }
            />
          </Box>
        )}
        <Box className="items-section items-section--pricing">
          {pricingItems.map((item, index) =>
            renderPricingItem({
              index,
              currencyCode,
              ...item,
            }),
          )}
        </Box>
        <Box className="items-section items-section--summary">
          {summaryItems.map((item, index) => renderSummaryItem(index, item, !isVesper || item.type === "rewards"))}
        </Box>
      </Box>
      {isFreeCancellation && cancellationPolicyProps && (
        <DesktopPopupModal
          open={openCancellationPolicyModal}
          onClose={(event: React.MouseEvent) => {
            event.stopPropagation();
            setOpenCancellationPolicyModal(false);
          }}
          className={clsx(
            "desktop-experiences-shop-cancellation-policy-modal-root",
            { xmod: isXmodRedesign },
          )}
          contentClassName="desktop-cancellation-policy-modal-wrapper"
          invisibleBackdrop={false}
        >
          <div className="experiences-shop-section-container">
            <Typography className="experiences-shop-component-title">
              {CANCELLATION_POLICY}
            </Typography>
            <ShopCancellationPolicy
              cancellationPolicy={cancellationPolicyProps.cancellationPolicy}
              selectedDate={cancellationPolicyProps.selectedDate}
              selectedTime={cancellationPolicyProps.selectedTime}
            />
          </div>
        </DesktopPopupModal>
      )}
    </>
  );
};
